<template>
  <transition appear name="modal">
    <div class="l-modal">
      <div class="modal__outer">
        <div class="modal__inner" @click.self="onClickOutside">
          <div :class="containerClassName">
            <h2 class="modal__title" v-if="title">
              {{ title }}
            </h2>

            <div :class="loadingContainerClassName">
              <p>{{ message }}</p>
              <h3 v-if="item">{{ item }}</h3>
              <p v-if="description" class="description">{{ description }}</p>

              <slot name="content" />

              <slot name="footer">
                <div class="modal__bottom">
                  <Button v-if="isConfirm" @click="onCancel">
                    {{ cancelText }}
                  </Button>
                  <slot name="okBtn">
                    <Button
                      :disabled="okDisabled"
                      :primary="!isCaution && isConfirm"
                      :caution="isCaution"
                      @click="onOk"
                      >{{ okText }}</Button
                    >
                  </slot>
                </div>
              </slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { noop } from 'lodash'
import Vue from 'vue'
import Button from '@/components/atoms/Button'

/**
 * ダイアログ
 * @deprecated [ModalDialog](#modal) を使用して下さい
 */
const Dialog = {
  name: 'Dialog',
  components: {
    Button,
  },
  props: {
    /** タイトル */
    title: { type: String, default: '' },
    /** メッセージ */
    message: { type: String, default: '' },
    /** 変更内容等テキスト */
    item: { type: String, default: '' },
    /** 説明文テキスト */
    description: { type: String, default: '' },
    /** @deprecated */
    containerClassName: { type: Array, default: () => ['modal__container', ' container--dialogue'] }, // TODO いずれ廃止する
    /** @deprecated */
    loadingContainerClassName: { type: Array, default: () => ['modal__confirm'] }, // TODO いずれ廃止する
    /** 注意喚起フラグ */
    isCaution: { type: Boolean, default: false },
    /** キャンセルボタンを出すかどうか */
    isConfirm: { type: Boolean, default: false },
    /** キャンセルボタンのテキスト */
    cancelText: { type: String, default: 'キャンセル' },
    /** OKボタンのテキスト */
    okText: { type: String, default: 'OK' },
    /** OKボタンの非活性フラグ */
    okDisabled: { type: Boolean, default: false },
  },
  methods: {
    onOk() {
      /**
       * OKボタンクリック
       * @event onOk
       */
      this.$emit('onOk')
      this.onClose()
    },
    onCancel() {
      /**
       * キャンセルボタンクリック
       * @event onCancel
       */
      this.$emit('onCancel')
      this.onClose()
    },
    onClickOutside() {
      /**
       * ダイアログ外クリック
       * @event clickOutside
       */
      this.$emit('clickOutside')
      this.onClose()
    },
    onClose() {
      /**
       * クローズ
       * @event close
       */
      this.$emit('close')
    },
  },
}

export default Dialog

/**
 * 確認ダイアログを表示します
 *
 * @param {string} message メッセージ
 * @param {string} [title] タイトル
 * @param {string} [item] 確認の対象となる項目名
 * @param {string} [description] 説明文テキスト
 * @param {string} [isCaution] 注意喚起フラグ
 * @param {string} [okText=OK] OKボタンのテキスト
 * @param {string} [cancelText=キャンセル] キャンセルボタンのテキスト
 * @param {function} [onOk] OKボタンクリックのハンドラ
 * @param {function} [onCancel] キャンセルボタンクリックのハンドラ
 */
export function confirmDialog({
  title,
  message,
  item,
  description,
  isCaution = false,
  okText = 'OK',
  cancelText = 'キャンセル',
  onOk = noop,
  onCancel = noop,
}) {
  const container = document.body
  const Component = Vue.extend(Dialog)
  return new Promise(resolve => {
    const dialog = new Component({
      propsData: {
        title,
        message,
        item,
        description,
        isCaution,
        isConfirm: true,
        okText,
        cancelText,
      },
    })
      .$on('onOk', () => resolve(true) || onOk())
      .$on('onCancel', () => resolve(false) || onCancel())
      .$on('clickOutside', () => resolve(false) || onCancel())
      .$on('close', () => {
        dialog.$el.style.opacity = 0 // TODO transition に任せたい
        setTimeout(() => {
          dialog.$el.remove()
          dialog.$destroy()
        }, 300)
      })
      .$mount()
    container.appendChild(dialog.$el)
  })
}

/**
 * 文章、ボタン1つのダイアログを表示します
 *
 * @param {string} message メッセージ
 * @param {string} [title] タイトル
 * @param {string} [okText=OK] OKボタンのテキスト
 * @param {function} [onOk] OKボタンクリックのハンドラ
 */
export function showDialog({ title, message, item, okText = 'OK', onOk = noop }) {
  const container = document.body
  const Component = Vue.extend(Dialog)
  return new Promise(resolve => {
    const dialog = new Component({
      propsData: {
        title,
        message,
        item,
        okText,
      },
    })
      .$on('onOk', () => resolve(true) || onOk())
      .$on('clickOutside', () => resolve(true) || onOk())
      .$on('close', () => {
        dialog.$el.style.opacity = 0 // TODO transition に任せたい
        setTimeout(() => {
          dialog.$el.remove()
          dialog.$destroy()
        }, 300)
      })
      .$mount()
    container.appendChild(dialog.$el)
  })
}

/**
 * 削除確認ダイアログ
 *
 * @param {string} target 削除対象の機能名
 * @param {string} item 削除対象の項目名
 * @param {(string|function)} description 説明文テキスト ※又は削除ボタンクリックのハンドラ
 * @param {function} [onOk] 削除ボタンクリックのハンドラ
 */
export function confirmDeleteDialog(target, item, description, onOk) {
  if (typeof description === 'function') {
    onOk = description
    description = null
  }

  return confirmDialog({
    title: `${target}削除`,
    message: `以下の${target}を削除してもよろしいですか？`,
    item,
    description,
    okText: '削除',
    cancelText: '戻る',
    isCaution: true,
    onOk,
  })
}

/**
 * 破棄確認ダイアログ
 *
 * @param {function} onOk OKボタンクリックのハンドラ
 */
export function confirmDisposeDialog(onOk) {
  return confirmDialog({
    title: '確認',
    message: '登録中のデータを\n破棄してもよろしいですか？',
    okText: 'はい',
    isCaution: true,
    onOk,
  })
}

/**
 * 予約中止ダイアログ
 */
export function leaveReserveDialog(onOk) {
  return confirmDialog({
    title: '予約は完了していません',
    message: '予約の登録を中止してよろしいですか？',
    okText: '中止する',
    cancelText: '戻る',
    isCaution: true,
    onOk,
  })
}
</script>

<style lang="postcss" scoped>
.l-modal {
  background-color: rgba(0, 0, 0, 0.4);
  display: block;
  height: 100vh;
  left: 0;
  min-width: 1024px;
  overflow: hidden;
  position: fixed;
  top: 0;
  transition: opacity 0.3s ease;
  width: 100vw;
  z-index: 2000;

  .modal__outer {
    display: table;
    height: 100%;
    width: 100%;
  }

  .modal__inner {
    display: table-cell;
    padding: 24px;
    transition: all 0.3s ease;
    vertical-align: middle;
  }

  .modal__container {
    background-color: var(--white);
    box-sizing: border-box;
    margin-left: auto;
    margin-right: auto;
    max-width: 960px;
    padding-top: 18px;
    position: relative;
    width: 480px;

    &.container--dialogue {
      padding-top: 40px;
    }
  }

  .modal__close {
    background: url(/assets/img/icons/close.png) no-repeat center center;
    background-size: 14px 14px;
    display: block;
    height: 24px;
    position: absolute;
    right: 16px;
    text-indent: -9999px;
    top: 16px;
    width: 24px;
  }

  .modal__title {
    color: var(--very-dark-gray-2);
    font-size: 21px;
    line-height: 1;
    text-align: center;
  }

  .modal__bottom {
    display: inline-flex;
    margin: 16px auto 0;

    .button {
      margin-right: 32px;

      &:last-child {
        margin-right: 0;
      }
    }
  }

  .modal__confirm {
    color: var(--text-color);
    font-size: 15px;
    padding: 24px 32px;
    text-align: center;

    p {
      white-space: pre-line;
    }

    .modal__bottom {
      margin: 32px auto 0;
    }
  }
}

h3 {
  font-size: 15px;
  font-weight: 600;
  margin-top: 16px;
  white-space: pre-wrap;
}

.description {
  font-size: 11px;
  margin-top: 16px;
}

.modal__bottom button {
  min-width: 160px;
  padding: 0 16px;
  width: auto;
}

/* 以下 transition 用 */
.modal-enter {
  opacity: 0;
}

.modal-leave-active {
  opacity: 0;
}
</style>
